import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useReactFlow } from "reactflow";

import {
  getId,
  findFirstMissingNumber,
  calculateCenterCoordinate,
  isNodeIdIncludedOnEdges,
  hasSameDirection,
} from "../utilities/helper";

//* Node Utils
import nodeUtils from "../utilities/nodeUtils";

//* Import Autolayout
import AutoLayout from "../nodes/features/AutoLayout";

const NodeMenu = ({
  node = {},
  top,
  left,
  selectedData = { nodes: [], edges: [] },
  gameNumbers = [],
  hasWorkingTeam,
  ...props
}) => {
  const {
    labels,
    unSelectNodes,
    updateUsedTitles,
    updateDuplicateGames,
    deleteSingleNode,
    deleteMultipleNode,
    duplicateSingleNode,
    duplicateMultipleNode,
    cleanupSelected,
  } = nodeUtils();
  const { addNodes, setEdges, getEdges } = useReactFlow();
  const [showCleanup, setShowCleanup] = useState(false);
  const [direction, setDirection] = useState(false);
  const [showWinner, setShowWinner] = useState(false);

  //* Layout
  const { updateLayout } = AutoLayout();

  const gameNodes = useMemo(() => {
    return (
      selectedData.nodes.filter(
        (item) =>
          item.data.game_number !== undefined && item.type == "defaultNode"
      ).length === 2
    );
  }, [selectedData.nodes]);

  const showCompassWinnerLoser = useMemo(() => {
    if (selectedData.nodes.length > 1) return false;

    if (node.type == "compassNode") {
      const compassEdges = getEdges().filter((edge) => edge.source == node.id);

      const result = compassEdges.reduce(
        (acc, item) => {
          acc.sourceA ||= item.sourceHandle === "source-a";
          acc.sourceB ||= item.sourceHandle === "source-b";
          return acc;
        },
        { sourceA: false, sourceB: false }
      );

      return result;
    }

    return false;
  }, [selectedData.nodes, node]);

  useEffect(() => {
    if (
      node &&
      node.id &&
      node.type != "compassNode" &&
      selectedData.nodes.length <= 1
    ) {
      setDirection(node.sourcePosition == "right" ? "LR" : "RL");
    }
  }, [node]);

  useEffect(() => {
    const { nodes } = selectedData;

    const position = hasSameDirection(nodes);

    const hasCompassNode = nodes.some(
      (element) => element.type === "compassNode"
    );

    if (
      hasCompassNode ||
      (nodes.length == 1 && nodes[0].type == "compassNode")
    ) {
      setDirection(false);
    } else if (position) {
      setDirection(position);
    }

    const isValid = isNodeIdIncludedOnEdges(selectedData);
    setShowCleanup(isValid);

    const edges = getEdges();
    const isValidWinner = isNodeIdIncludedOnEdges(
      {
        nodes,
        edges,
      },
      true
    );

    setShowWinner(isValidWinner && position && gameNodes);
  }, [selectedData.nodes]);

  //* Duplicate node
  const duplicateNode = useCallback(() => {
    const duplicateData = async () => {
      if (selectedData.nodes.length > 1) {
        await duplicateMultipleNode(selectedData);
      } else {
        const nodeId = node.id ? node.id : selectedData.nodes[0].id;
        await duplicateSingleNode(nodeId);
      }
      await updateDuplicateGames();
    };
    duplicateData();
  }, [node, selectedData.nodes]);

  //* Delete node
  const deleteNode = useCallback(() => {
    const deleteData = async () => {
      if (selectedData.nodes.length >= 2) {
        await deleteMultipleNode(selectedData);
      } else {
        const nodeId = node.id ? node.id : selectedData.nodes[0].id;
        await deleteSingleNode(nodeId);
      }
      await updateDuplicateGames();
    };
    deleteData();
  }, [node, selectedData.nodes]);

  //* Create winner
  const createWinner = useCallback(() => {
    const addWinnerNode = async () => {
      // const [node1, node2] = selectedData.nodes.sort(
      //   (a, b) => a.data.game_number - b.data.game_number
      // );

      const [node1, node2] = selectedData.nodes
        .slice()
        .sort((a, b) => a.position.y - b.position.y);

      const { newNumber } = findFirstMissingNumber(gameNumbers);
      const position = calculateCenterCoordinate(
        node1.position,
        node2.position,
        direction
      );

      const targetPosition = direction === "RL" ? "right" : "left";
      const sourcePosition = direction === "RL" ? "left" : "right";

      const newNode = {
        id: getId(),
        type: "defaultNode",
        data: {
          team_1_of_type: "winner",
          team_1_of_type_value: node1.data.game_number,
          team_1_of_type_label: `${labels["winner"]} ${node1.data.game_number}`,
          team_1_cancelled: true,
          team_2_of_type: "winner",
          team_2_of_type_value: node2.data.game_number,
          team_2_of_type_label: `${labels["winner"]} ${node2.data.game_number}`,
          team_2_cancelled: true,
          ...(hasWorkingTeam
            ? {
                hasWorkingTeam: hasWorkingTeam,
                team_3_of_type: "",
                team_3_of_type_label: "",
                team_3_of_type_value: "",
                team_3_cancelled: true,
              }
            : {}),
          game_number: newNumber,
        },
        sourcePosition,
        targetPosition,
        position,
        selected: true,
      };

      //* Add new edge
      const createEdge = (source, targetHandle) => ({
        id: `edge_${Date.now()}_${source.id}`,
        source: source.id,
        sourceHandle: "source-a",
        target: newNode.id,
        targetHandle,
        type: "smoothstep",
      });

      //* Deselect the selected nodes
      unSelectNodes();

      //* Add new node/edges
      addNodes(newNode);
      setEdges((prevEdges) => [
        ...prevEdges,
        createEdge(node1, "target-a"),
        createEdge(node2, "target-b"),
      ]);

      await updateUsedTitles();
    };

    addWinnerNode();
  }, [selectedData.nodes, direction]);

  //* Create what if game
  const showWhatIfGame = useMemo(() => {
    if (
      node &&
      node.id &&
      selectedData.nodes.length <= 1 &&
      node.type == "defaultNode"
    ) {
      const edges = getEdges();
      const isShow = isNodeIdIncludedOnEdges(
        {
          nodes: [node],
          edges,
        },
        true
      );

      return isShow;
    }
    return false;
  }, [node]);

  const createWhatIfGame = useCallback(() => {
    if (node && node.id) {
      const { newNumber } = findFirstMissingNumber(gameNumbers);
      const newNode = {
        id: getId(),
        type: "whatIfNode",
        data: {
          team_1_of_type: "winner",
          team_1_of_type_value: node.data.game_number,
          team_1_of_type_label: `${labels["winner"]} ${node.data.game_number}`,
          team_1_cancelled: node.data.team_1_cancelled,
          team_2_of_type: "loser",
          team_2_of_type_value: node.data.game_number,
          team_2_of_type_label: `${labels["loser"]} ${node.data.game_number}`,
          team_2_cancelled: node.data.team_2_cancelled,
          ...(hasWorkingTeam
            ? {
                hasWorkingTeam: hasWorkingTeam,
                team_3_of_type: "",
                team_3_of_type_label: "",
                team_3_of_type_value: "",
                team_3_cancelled: true,
              }
            : {}),
          game_number: newNumber,
        },
        targetPosition: node.sourcePosition,
        sourcePosition: node.sourcePosition,
        position: {
          x: node.position.x,
          y: node.position.y + node.height + 100,
        },
      };

      const newEdge = {
        id: `edge_${Date.now()}_${node.id}`,
        source: node.id,
        sourceHandle: "source-a",
        target: newNode.id,
        targetHandle: "target-a",
        type: "dottedSmoothStepEdge",
      };

      //* Add new node/edge
      addNodes(newNode);
      setEdges((edges) => [...edges, newEdge]);
    }
  }, [node]);

  //* Change direction
  const changeDirection = (direction, cleanup = false) => {
    if (selectedData.nodes.length > 1) {
      updateLayout(selectedData, direction, cleanup);
    } else {
      const nodeData = node && node.id ? node : selectedData.nodes[0];
      updateLayout({ nodes: [nodeData], edges: [] }, direction, cleanup);
    }
  };

  const cleanupData = useCallback(() => {
    const hasCompassNode = selectedData.nodes.some(
      (element) => element.type === "compassNode"
    );
    if (hasCompassNode) {
      cleanupSelected(selectedData);
    } else {
      changeDirection(direction, true);
    }
  }, [direction]);

  const createCompassWinnerLoser = (type) => {
    const { newNumber } = findFirstMissingNumber(gameNumbers);
    let newNode = {
      id: getId(),
      type: "defaultNode",
      data: {
        team_1_of_type: type,
        team_1_of_type_value: node.data.game_number,
        team_1_of_type_label: `${labels[type]} ${node.data.game_number}`,
        team_1_cancelled: true,
        team_2_of_type: "",
        team_2_of_type_value: "",
        team_2_of_type_label: "",
        team_2_cancelled: true,
        ...(hasWorkingTeam
          ? {
              hasWorkingTeam: hasWorkingTeam,
              team_3_of_type: "",
              team_3_of_type_label: "",
              team_3_of_type_value: "",
              team_3_cancelled: true,
            }
          : {}),
        game_number: newNumber,
      },
      position: {
        x: node.position.x,
        y: node.position.y + node.height / 2 + 30,
      },
      targetPosition: "left",
      sourcePosition: "right",
    };

    let newEdge = {
      id: `edge_${Date.now()}_${node.id}`,
      source: node.id,
      sourceHandle: "source-b",
      target: newNode.id,
      targetHandle: "target-a",
      type: "smoothstep",
    };

    if (type == "winner") {
      newNode.position.x = node.position.x + node.width + 50;
    } else {
      newNode.position.x = node.position.x - node.width - 50;
      newNode.targetPosition = "right";
      newNode.sourcePosition = "left";
      newEdge.sourceHandle = "source-a";
    }

    addNodes(newNode);
    setEdges((edges) => [...edges, newEdge]);
  };

  return (
    <div style={{ top, left }} className="contextMenu" {...props}>
      {showWinner && <button onClick={createWinner}>Add Winner</button>}

      {showWhatIfGame && (
        <button onClick={createWhatIfGame}>Add What if game</button>
      )}

      {direction && (
        <button
          onClick={() => changeDirection(direction === "LR" ? "RL" : "LR")}
        >
          {direction === "LR" ? "Right to Left" : "Left to Right"}
        </button>
      )}

      {showCleanup && (
        <>
          {/* <button onClick={() => changeDirection("LR", true)}>
            Cleanup Layout
          </button> */}
          <button onClick={cleanupData}>Cleanup</button>
        </>
      )}
      {showCompassWinnerLoser && (
        <>
          {!showCompassWinnerLoser.sourceB && (
            <button onClick={() => createCompassWinnerLoser("winner")}>
              Add Winner
            </button>
          )}
          {!showCompassWinnerLoser.sourceA && (
            <button onClick={() => createCompassWinnerLoser("loser")}>
              Add Loser
            </button>
          )}
        </>
      )}
      <button onClick={duplicateNode}>Duplicate</button>
      <button onClick={deleteNode}>Delete</button>
    </div>
  );
};

export default NodeMenu;
